import { z } from 'zod';
import { uuidSchema } from '../common';

// =============================================================================
// Resource params & queries
// =============================================================================

export const resourceIdParam = z.object({ id: uuidSchema });

export const listResourcesQuery = z.object({
  hub_id: uuidSchema,
  enabled: z.enum(['true', 'false']).optional(),
  resource_type: z.enum(['knowledge', 'skill']).optional(),
  search: z.string().optional(),
  limit: z.string().regex(/^\d+$/).optional(),
  offset: z.string().regex(/^\d+$/).optional(),
});

export const createResourceBody = z.object({
  hub_id: uuidSchema,
  resource_name: z.string().min(1),
  resource_description: z.string().optional(),
  enabled: z.boolean().optional(),
  folder_metadata_schema: z.record(z.unknown()).optional(),
  file_metadata_schema: z.record(z.unknown()).optional(),
  resource_type: z.enum(['knowledge', 'skill']).optional(),
  skill_name: z.string().optional(),
  skill_version: z.string().optional(),
});

export const updateResourceBody = z.object({
  resource_name: z.string().min(1).optional(),
  resource_description: z.string().optional(),
  enabled: z.boolean().optional(),
  folder_metadata_schema: z.record(z.unknown()).optional(),
  file_metadata_schema: z.record(z.unknown()).optional(),
  skill_name: z.string().optional(),
  skill_version: z.string().optional(),
  // No `anthropic_skill_id`/`anthropic_version`: `resource` has neither column.
  // The provider link lives on `resource_provider_sync.provider_skill_id`,
  // written by `skillSync.ts` — never through this body.
});

export const syncSkillsBody = z.object({
  hub_id: uuidSchema,
  connection_id: uuidSchema.optional(),
});

// =============================================================================
// Resource File params & queries
// =============================================================================

export const resourceFileIdParam = z.object({ id: uuidSchema });

export const listResourceFilesQuery = z.object({
  hub_id: uuidSchema,
  resource_id: uuidSchema.optional(),
  folder_id: z.string().optional(), // 'null' for root, UUID for specific folder
  search: z.string().optional(),
  tags: z.string().optional(), // comma-separated
  enabled: z.enum(['true', 'false']).optional(),
  limit: z.string().regex(/^\d+$/).optional(),
  offset: z.string().regex(/^\d+$/).optional(),
  include_root_files: z.enum(['true', 'false']).optional(),
});

export const createResourceFileBody = z.object({
  hub_id: uuidSchema,
  resource_id: uuidSchema,
  file_id: uuidSchema,
  folder_id: uuidSchema.nullable().optional(),
  resource_file_title: z.string().optional(),
  resource_file_tags: z.array(z.string()).optional(),
  order_index: z.number().optional(),
  enabled: z.boolean().optional(),
  metadata: z.record(z.unknown()).optional(),
});

export const updateResourceFileBody = z.object({
  resource_file_title: z.string().optional(),
  resource_file_tags: z.array(z.string()).optional(),
  folder_id: uuidSchema.nullable().optional(),
  order_index: z.number().optional(),
  enabled: z.boolean().optional(),
  metadata: z.record(z.unknown()).optional(),
});

export const uploadResourceFileBody = z.object({
  hub_id: uuidSchema,
  resource_id: uuidSchema,
  folder_id: uuidSchema.nullable().optional(),
  file_name: z.string().min(1),
  file_data: z.string().min(1),
  mime_type: z.string().optional(),
  file_size: z.number().optional(),
  tags: z.array(z.string()).optional(),
});

export const uploadSkillZipBody = z.object({
  hub_id: uuidSchema,
  resource_id: uuidSchema,
  zip_data: z.string().min(1),
  replace_existing: z.boolean().optional(),
});

// =============================================================================
// Resource Folder params & queries
// =============================================================================

export const resourceFolderIdParam = z.object({ id: uuidSchema });

export const listResourceFoldersQuery = z.object({
  hub_id: uuidSchema,
  resource_id: uuidSchema,
  parent_folder_id: z.string().optional(), // 'null' for root folders
  enabled: z.enum(['true', 'false']).optional(),
});

export const createResourceFolderBody = z.object({
  hub_id: uuidSchema,
  resource_id: uuidSchema,
  folder_name: z.string().min(1),
  folder_description: z.string().optional(),
  parent_folder_id: uuidSchema.nullable().optional(),
  order_index: z.number().optional(),
  enabled: z.boolean().optional(),
  metadata: z.record(z.unknown()).optional(),
});

export const updateResourceFolderBody = z.object({
  folder_name: z.string().min(1).optional(),
  folder_description: z.string().optional(),
  parent_folder_id: uuidSchema.nullable().optional(),
  order_index: z.number().optional(),
  enabled: z.boolean().optional(),
  metadata: z.record(z.unknown()).optional(),
});

// =============================================================================
// Agent Resource params & queries
// =============================================================================

export const agentResourceQuery = z.object({
  hub_id: uuidSchema,
  agent_id: uuidSchema.optional(),
  resource_id: uuidSchema.optional(),
}).refine(data => data.agent_id || data.resource_id, {
  message: 'Either agent_id or resource_id is required',
});

export const hubResourceQuery = z.object({ hub_id: uuidSchema });

export const linkAgentResourceBody = z.object({
  hub_id: uuidSchema,
  resource_id: uuidSchema,
  agent_id: uuidSchema,
  priority: z.number().optional(),
  use_native_integration: z.boolean().optional(),
  write_enabled: z.boolean().optional(),
});

export const updateAgentResourceBody = z.object({
  hub_id: uuidSchema,
  resource_id: uuidSchema,
  agent_id: uuidSchema,
  use_native_integration: z.boolean().optional(),
  enabled: z.boolean().optional(),
  priority: z.number().optional(),
  write_enabled: z.boolean().optional(),
});

export const unlinkAgentResourceQuery = z.object({
  hub_id: uuidSchema,
  resource_id: uuidSchema,
  agent_id: uuidSchema,
});

// =============================================================================
// Inferred types
// =============================================================================

export type CreateResourceInput = z.infer<typeof createResourceBody>;
export type UpdateResourceInput = z.infer<typeof updateResourceBody>;
export type CreateResourceFileInput = z.infer<typeof createResourceFileBody>;
export type UpdateResourceFileInput = z.infer<typeof updateResourceFileBody>;
export type UploadResourceFileInput = z.infer<typeof uploadResourceFileBody>;
export type UploadSkillZipInput = z.infer<typeof uploadSkillZipBody>;
export type CreateResourceFolderInput = z.infer<typeof createResourceFolderBody>;
export type UpdateResourceFolderInput = z.infer<typeof updateResourceFolderBody>;
export type LinkAgentResourceInput = z.infer<typeof linkAgentResourceBody>;
export type UpdateAgentResourceInput = z.infer<typeof updateAgentResourceBody>;

// =============================================================================
// RESPONSE TYPES — Resource
// =============================================================================

/** A single resource row (select *) */
export interface ResourceResponse {
  resource_id: string;
  created_at: string;
  updated_at: string;
  resource_name: string;
  resource_description: string | null;
  hub_id: string;
  created_by: string;
  enabled: boolean;
  folder_metadata_schema: Record<string, unknown> | null;
  file_metadata_schema: Record<string, unknown> | null;
  resource_type: 'knowledge' | 'skill';
  skill_name: string | null;
  skill_version: string | null;
}

export interface ListResourcesResponse {
  success: true;
  data: ResourceResponse[];
  total_count: number;
}

export interface GetResourceResponse {
  success: true;
  data: ResourceResponse;
}

export interface CreateResourceResponse {
  success: true;
  data: ResourceResponse;
}

export interface UpdateResourceResponse {
  success: true;
  data: ResourceResponse;
}

export interface DeleteResourceResponse {
  success: true;
  message: string;
}

// =============================================================================
// RESPONSE TYPES — Resource File
// =============================================================================

/** Joined file metadata returned with resource_file list queries */
export interface ResourceFileJoinedFile {
  file_id: string;
  file_name: string;
  file_path: string;
  bucket: string;
  mime_type: string;
  file_size: number;
}

/** Joined file metadata returned with resource_file read queries (includes file_text) */
export interface ResourceFileJoinedFileWithText extends ResourceFileJoinedFile {
  file_text: string | null;
}

/** A resource_file row with joined file data (list variant) */
export interface ResourceFileResponse {
  resource_file_id: string;
  created_at: string;
  updated_at: string;
  resource_file_title: string;
  resource_file_tags: string[] | null;
  created_by: string;
  enabled: boolean;
  folder_id: string | null;
  file_id: string;
  order_index: number;
  resource_id: string;
  metadata: Record<string, unknown> | null;
  file_path: string | null;
  file: ResourceFileJoinedFile | null;
  /**
   * The agent-facing address of this file — `resources/<slug>/<folders…>/<name>`,
   * exactly what `read_file`, `send_files` and `list_files` accept.
   *
   * SERVER-COMPUTED, deliberately. A client cannot derive it: the resource slug
   * carries a collision suffix ranked across every resource in the hub, and the
   * relative path falls back through the folder chain, the authored `file_path`
   * and the leaf name. Recomputing any of that in the browser would produce an
   * address that drifts from the one the tools resolve — which is the whole
   * failure this path-addressing work exists to remove.
   *
   * Null when the path could not be derived (a disabled row, or a transient
   * folder read failure that degrades paths rather than failing the listing).
   */
  canonical_path?: string | null;
}

/** A resource_file row with joined file data (read variant — includes file_text) */
export interface ResourceFileDetailResponse {
  resource_file_id: string;
  created_at: string;
  updated_at: string;
  resource_file_title: string;
  resource_file_tags: string[] | null;
  created_by: string;
  enabled: boolean;
  folder_id: string | null;
  file_id: string;
  order_index: number;
  resource_id: string;
  metadata: Record<string, unknown> | null;
  file_path: string | null;
  file: ResourceFileJoinedFileWithText | null;
}

/** A resource_file row without joined file data (create/update return select *) */
export interface ResourceFileRowResponse {
  resource_file_id: string;
  created_at: string;
  updated_at: string;
  resource_file_title: string;
  resource_file_tags: string[] | null;
  created_by: string;
  enabled: boolean;
  folder_id: string | null;
  file_id: string;
  order_index: number;
  resource_id: string;
  metadata: Record<string, unknown> | null;
  file_path: string | null;
}

export interface ListResourceFilesResponse {
  success: true;
  data: ResourceFileResponse[];
  total_count: number;
}

export interface GetResourceFileResponse {
  success: true;
  data: ResourceFileDetailResponse;
}

export interface CreateResourceFileResponse {
  success: true;
  data: ResourceFileRowResponse;
}

export interface UpdateResourceFileResponse {
  success: true;
  data: ResourceFileRowResponse;
}

export interface DeleteResourceFileResponse {
  success: true;
  message: string;
}

export interface UploadResourceFileResponse {
  success: true;
  data: {
    fileId: string;
    fileName: string;
    fileUrl: string;
    resourceFileId: string;
  };
}

export interface UploadSkillZipResponse {
  success: boolean;
  files_uploaded: number;
  folders_created: number;
  frontmatter: Record<string, string | undefined>;
  errors: string[];
  warnings: string[];
  root_folder_stripped?: string;
}

// =============================================================================
// RESPONSE TYPES — Resource Folder
// =============================================================================

/** A single resource_folder row (select *) */
export interface ResourceFolderResponse {
  resource_folder_id: string;
  created_at: string;
  updated_at: string;
  folder_name: string;
  folder_description: string | null;
  order_index: number;
  parent_folder_id: string | null;
  resource_id: string;
  created_by: string;
  enabled: boolean;
  metadata: Record<string, unknown> | null;
}

export interface ListResourceFoldersResponse {
  success: true;
  data: ResourceFolderResponse[];
  total_count: number;
}

export interface GetResourceFolderResponse {
  success: true;
  data: ResourceFolderResponse;
}

export interface CreateResourceFolderResponse {
  success: true;
  data: ResourceFolderResponse;
}

export interface UpdateResourceFolderResponse {
  success: true;
  data: ResourceFolderResponse;
}

export interface DeleteResourceFolderResponse {
  success: true;
  message: string;
}

// =============================================================================
// RESPONSE TYPES — Agent Resource
// =============================================================================

/** A single agent_resource row (select *) */
export interface AgentResourceRowResponse {
  agent_resource_id: string;
  agent_id: string;
  resource_id: string;
  priority: number;
  enabled: boolean;
  created_at: string;
  use_native_integration: boolean;
  write_enabled?: boolean;
}

/** Provider sync info for a skill resource (flattened from nested join) */
export interface AgentResourceProviderSync {
  sync_id: string;
  sync_status: 'pending' | 'synced' | 'failed';
  last_sync_at: string | null;
  service_name: string;
}

/** Flattened agent-resource with resource details (returned by getAgentResources) */
export interface AgentResourceLink {
  agent_resource_id: string;
  resource_id: string;
  resource_name: string;
  resource_description: string | null;
  resource_type: 'knowledge' | 'skill';
  priority: number;
  enabled: boolean;
  use_native_integration: boolean;
  write_enabled?: boolean;
  provider_syncs?: AgentResourceProviderSync[];
}

/** Agent info attached to a resource (returned by getResourceAgents) */
export interface ResourceAgentLink {
  agent_id: string;
  priority: number;
  enabled: boolean;
  agent: {
    agent_id: string;
    agent_name: string;
    agent_role: string;
  } | null;
}

export interface GetAgentResourcesResponse {
  success: true;
  resources: AgentResourceLink[];
}

export interface GetResourceAgentsResponse {
  data: ResourceAgentLink[];
}

/** Hub resource summary (for selection dropdowns) */
export interface HubResourceSummary {
  resource_id: string;
  resource_name: string;
  resource_description: string | null;
  resource_type: 'knowledge' | 'skill';
  enabled: boolean;
}

export interface ListHubResourcesResponse {
  success: true;
  resources: HubResourceSummary[];
  total_count: number;
}

export interface LinkAgentResourceResponse {
  success: true;
  link: AgentResourceRowResponse;
}

export interface UpdateAgentResourceResponse {
  success: true;
  link: AgentResourceRowResponse;
}

export interface UnlinkAgentResourceResponse {
  success: true;
  message: string;
}

// =============================================================================
// RESPONSE TYPES — Skill Sync
// =============================================================================

export interface SyncSkillsResultItem {
  connection_id: string;
  connection_name: string;
  uploaded: number;
  failed: number;
  errors: string[];
}

export interface SyncSkillsResponse {
  success: true;
  message: string;
  results: SyncSkillsResultItem[];
}
